import React, { useEffect, useMemo, useRef, useState } from "react";
import { TimesheetDetailsTabSkeleton } from "../../../components/TimesheetRevamp/TimesheetDetailTabSkeletons";
import {
  convertTimeToDatetimeLocal,
  convertTimeToDatetimeLocalForTesting,
  convertTimeToDatetimeLocalWithoutMoment,
  customFormatDate,
  dateModifiedForTimesheet,
  extractTimeFromDateTime,
  isCheckNextDayShift,
  isEndTimeNextDay,
  manuallyEmbedDateWithTime,
  parseBooleanVal,
} from "Utils/utils";
import { isV5MultiAssignmentTimesheet } from "../NewCodeAllocation/v5BillingUtils";
import moment from "moment";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "store/selector/auth.selector";
import { roleAccess } from "helpers/RolesPermission";
import { USER_ROLE } from "Constants/constant";
import useCareLabels from "hooks/useCareLabels";
import { apiPostRequest } from "helpers/Requests";
import { URL_ROSTERING_TIMESHEET_DETAILS_UPDATE } from "store/services/URL";
import { toast } from "react-toastify";
import { FaPencilAlt } from "react-icons/fa";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import "react-datepicker/dist/react-datepicker.css";
import TimesheetModeBanner from "../../components/TimesheetModeBanner";
import { TOAST_IDS } from "store/constants";
import { syncTimesheetTableRowFromDetail } from "../../../utils/syncTimesheetTableRow";
import {
  buildShiftDateTime,
  getShiftDurationMinutesFromParts,
  minutesToHHMM,
} from "../../../utils/billingShiftRange";
import "../../../components/TimesheetRevamp/timesheet-detail-revamp.css";
import {
  TimesheetSharedShiftStrip,
  TimesheetInlineDatePicker,
  formatDurationMinutes,
} from "../../../components/TimesheetRevamp/TimesheetTabPrimitives";
import {
  TimesheetTimeSection,
  TimesheetCompliancePanel,
  TimesheetCrossOverToggleRow,
  TimesheetSleepoverActiveHoursRow,
  TimesheetCostsPanel,
  TimesheetParticipantNotesPanel,
  TimesheetDetailsSaveBar,
} from "../../../components/TimesheetRevamp/TimesheetDetailsLayout";

/** Allow typing duration as HH:MM (digits + optional colon). Same rules as advance worker card. */
const sanitizeActiveHoursInput = (raw) => {
  const cleaned = String(raw ?? "").replace(/[^\d:]/g, "");
  const colonIndex = cleaned.indexOf(":");

  if (colonIndex === -1) {
    const digits = cleaned.slice(0, 4);
    if (digits.length <= 1) return digits;
    const hours = digits.slice(0, 2);
    let minutes = digits.slice(2, 4);
    if (minutes.length > 0) {
      const minsNum = Number(minutes);
      if (!Number.isNaN(minsNum) && minsNum > 59) {
        minutes = "59";
      }
    }
    return minutes.length > 0 ? `${hours}:${minutes}` : `${hours}:`;
  }

  const hours = cleaned.slice(0, colonIndex).replace(/\D/g, "").slice(0, 2);
  let minutes = cleaned
    .slice(colonIndex + 1)
    .replace(/\D/g, "")
    .slice(0, 2);
  if (minutes.length > 0) {
    const minsNum = Number(minutes);
    if (!Number.isNaN(minsNum) && minsNum > 59) {
      minutes = "59";
    }
  }
  return `${hours}:${minutes}`;
};

const getShiftNotesFromDetails = (details) => {
  if (!details) return [];
  const notes = details?.shift_notes ?? details?.shiftNotes ?? [];
  return Array.isArray(notes) ? notes : [];
};

const getParticipantNdis = (participant) =>
  participant?.ndis_number ||
  participant?.ndis_no ||
  participant?.ndisNo ||
  participant?.participant?.ndis_no ||
  participant?.participant?.ndis_number ||
  null;

const mergeParticipantsFromSources = (details, fallbackRow) => {
  const lists = [
    details?.participants,
    details?.shift_instruction,
    fallbackRow?.participants,
    (details?.shift_notes ?? []).map((note) => ({
      id: note.pt_id ?? note.participant_id ?? note.participant?.id,
      first_name: note.first_name ?? note.participant?.first_name,
      last_name: note.last_name ?? note.participant?.last_name,
      ndis_no: note.ndis_no ?? note.participant?.ndis_no,
      ndis_number: note.ndis_number ?? note.participant?.ndis_number,
    })),
  ].filter((list) => Array.isArray(list) && list.length > 0);

  if (lists.length === 0) return [];

  const byId = new Map();
  lists.forEach((list) => {
    list.forEach((participant) => {
      const id = participant?.id ?? participant?.pt_id;
      if (!id) return;

      const prev = byId.get(id) || {};
      const ndis = getParticipantNdis(participant) || getParticipantNdis(prev);

      byId.set(id, {
        ...prev,
        ...participant,
        ...(ndis
          ? { ndis_no: ndis, ndis_number: ndis }
          : {}),
      });
    });
  });

  return Array.from(byId.values());
};

const getParticipantsFromDetails = (details, fallbackRow) => {
  if (!details && !fallbackRow) return [];
  return mergeParticipantsFromSources(details, fallbackRow);
};

const clockHm = (value) => {
  if (value == null || value === "") return "";
  const parsed = moment(value, ["HH:mm", "HH:mm:ss", "h:mm A", "hh:mm A"], true);
  if (parsed.isValid()) return parsed.format("HH:mm");
  const str = String(value);
  return str.length >= 5 ? str.slice(0, 5) : str;
};

const extractTimeForInput = (datetimeStr) => {
  if (datetimeStr == null || datetimeStr === "") return "";
  if (typeof datetimeStr !== "string") {
    const parsed = moment(datetimeStr);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
  }
  const fromUtil = extractTimeFromDateTime(datetimeStr);
  if (fromUtil) return fromUtil;
  const parsed = moment(datetimeStr);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

const mergeTimesheetDetails = (shiftDetails, selectedTimesheet) => {
  if (!shiftDetails && !selectedTimesheet) return null;
  const participants = getParticipantsFromDetails(
    shiftDetails,
    selectedTimesheet
  );
  return {
    ...(selectedTimesheet || {}),
    ...(shiftDetails || {}),
    participants,
    shift_instruction: shiftDetails?.shift_instruction ?? [],
    shift_notes: getShiftNotesFromDetails(shiftDetails),
  };
};

const TimeSheetDetails = ({
  shiftDetails,
  setShiftDetails,
  selectedTimesheet,
  shiftDetailsLoader,
  isStatusApproved,
  loadingStatus,
}) => {
  const { participantLabel } = useCareLabels();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    clearErrors,
    reset,
  } = useForm();
  const watchFormData = watch();
  const dispatch = useDispatch();
  const currentLoginUser = useSelector(getUser);
  const [selectedShiftNotes, setSelectedShiftNotes] = useState({});
  const [notes, setNotes] = useState([]);
  const [shiftNotesErrors, setShiftNotesErrors] = useState({});
  // State to control End Date editability
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  // State to control Actual End Date editability
  const [isActualEndDatePickerOpen, setIsActualEndDatePickerOpen] =
    useState(false);
  const [selectedActualEndDate, setSelectedActualEndDate] = useState(null);
  const scheduledEndDateAnchorRef = useRef(null);
  const actualEndDateAnchorRef = useRef(null);
  const [crossOver, setCrossOver] = useState(false);
  const [showSplitResetWarning, setShowSplitResetWarning] = useState(false);
  const splitResetConfirmedRef = useRef(false);

  const effectiveDetails = useMemo(
    () => mergeTimesheetDetails(shiftDetails, selectedTimesheet),
    [shiftDetails, selectedTimesheet]
  );

  const isAdvanceTimesheet = useMemo(
    () => isV5MultiAssignmentTimesheet(effectiveDetails || shiftDetails),
    [effectiveDetails, shiftDetails]
  );

  const isBasicSplitShift = useMemo(
    () =>
      parseBooleanVal(effectiveDetails?.is_split_shift) && !isAdvanceTimesheet,
    [effectiveDetails?.is_split_shift, isAdvanceTimesheet]
  );


  // Shift-level: show for basic / basic split / advance when shift (or timesheet) sleepover is on.
  const showSleepoverSection = useMemo(
    () =>
      parseBooleanVal(effectiveDetails?.shift_sleepover) ||
      parseBooleanVal(effectiveDetails?.sleepover),
    [effectiveDetails?.shift_sleepover, effectiveDetails?.sleepover]
  );

  const schedule_time =
    effectiveDetails?.time_display_preference === "scheduled";

  useEffect(() => {
    if (!effectiveDetails) return;

    setSelectedShiftNotes({});
    setShiftNotesErrors({});
    const apiNotes = getShiftNotesFromDetails(effectiveDetails);
    if (apiNotes.length > 0) {
      setNotes(
        apiNotes.map((note) => ({
          ...note,
          pt_id: note.pt_id ?? note.participant_id ?? note.participant?.id,
          first_name: note.first_name ?? note.participant?.first_name,
          last_name: note.last_name ?? note.participant?.last_name,
          notes: note.notes ?? "",
        }))
      );
    } else {
      setNotes([]);
    }

    const crossOverValue =
      effectiveDetails?.cross_over === true ||
      effectiveDetails?.cross_over === 1 ||
      effectiveDetails?.cross_over === "1";
    setCrossOver(crossOverValue);

    const participants = getParticipantsFromDetails(
      effectiveDetails,
      selectedTimesheet
    );
    const instructions = effectiveDetails?.shift_instruction ?? [];
    const shiftInstructionsDefaults = participants.map((pt, index) => {
      const matched = instructions.find((si) => si.id === pt.id);
      return {
        pt_id: pt.id ?? "",
        shift_instruction: matched?.shift_instruction ?? "",
      };
    });

    const shiftSleepoverOn =
      parseBooleanVal(effectiveDetails?.shift_sleepover) ||
      parseBooleanVal(effectiveDetails?.sleepover);
    reset({
      startTime: extractTimeForInput(effectiveDetails?.start_time),
      endTime: extractTimeForInput(effectiveDetails?.end_time),
      actualStartTime: extractTimeForInput(effectiveDetails?.worker_start_time),
      actualEndTime: extractTimeForInput(effectiveDetails?.worker_end_time),
      incidentDetails: effectiveDetails?.incident ?? "",
      shiftCost: effectiveDetails?.comment ?? "",
      km: effectiveDetails?.km ?? "",
      shift_instructions: shiftInstructionsDefaults,
      // Shift-level sleepover: hydrate whenever the shift/timesheet is a sleepover.
      ...(shiftSleepoverOn
        ? {
            sleepover: true,
            active_hours: effectiveDetails?.active_hours
              ? String(effectiveDetails.active_hours)
              : "",
          }
        : {}),
    });
  }, [effectiveDetails, selectedTimesheet, reset]);

  const handleNoteChange = (index, value) => {
    const updatedNotes = [...notes];
    updatedNotes[index].notes = value;
    setNotes(updatedNotes);
  };
  const handleNewShiftNotes = (id, value) => {
    setSelectedShiftNotes((prevNotes) => {
      const updatedNotes = { ...prevNotes };

      if (value.trim()) {
        updatedNotes[id] = { id, note: value };
      } else {
        delete updatedNotes[id];
      }

      return updatedNotes;
    });
  };
  const [loader, setLoader] = useState(false);
  const [scheduleDate, setIsScheduleDate] = useState(null);
    const [actualDate, setActualDate] = useState(null);

 
  const getParticipantNoteText = (ptId, notesList, draftNotes) => {
    const noteObj = notesList?.find((n) => n.pt_id === ptId);
    const fromExisting = noteObj?.notes?.trim();
    if (fromExisting) return fromExisting;
    return draftNotes?.[ptId]?.note?.trim() || "";
  };

  const validateShiftNotes = (participantList, notesList, draftNotes) => {
    const nextErrors = {};
    participantList.forEach((participant) => {
      const ptId = participant?.id;
      if (!ptId) return;
      if (!getParticipantNoteText(ptId, notesList, draftNotes)) {
        nextErrors[ptId] = "Shift notes are required";
      }
    });
    return nextErrors;
  };

  /**
   * Resolve the calendar end date for save.
   * Cross-over must only bump to the next day when clock times wrap past midnight;
   * same-day shifts (e.g. 01:00–05:00) must keep their existing end date.
   */
  const resolveEndDateStrForSave = ({
    selectedDate,
    startDateStr,
    existingEndDateStr,
    startTime,
    endTime,
    crossOverEnabled,
  }) => {
    if (selectedDate) {
      return moment(selectedDate).format("DD/MM/YYYY");
    }

    let endDateStr = existingEndDateStr || startDateStr || "";
    if (
      !crossOverEnabled ||
      !startDateStr ||
      !startTime ||
      !endTime ||
      !endDateStr ||
      endDateStr !== startDateStr
    ) {
      return endDateStr;
    }

    const parseFormats = ["HH:mm", "hh:mm A", "h:mm A"];
    const tStart = moment(startTime, parseFormats, true);
    const tEnd = moment(endTime, parseFormats, true);
    if (
      tStart.isValid() &&
      tEnd.isValid() &&
      tEnd.diff(tStart, "minutes") <= 0 &&
      moment(startDateStr, "DD/MM/YYYY").isValid()
    ) {
      return moment(startDateStr, "DD/MM/YYYY")
        .add(1, "days")
        .format("DD/MM/YYYY");
    }

    return endDateStr;
  };

  const onSubmit = (data) => {
    if (!effectiveDetails?.uuid) return;

    const participantsForSave = effectiveDetails?.participants ?? [];
    const noteValidationErrors = validateShiftNotes(
      participantsForSave,
      notes,
      selectedShiftNotes
    );
    if (Object.keys(noteValidationErrors).length > 0) {
      setShiftNotesErrors(noteValidationErrors);
      toast.error(
        `Please enter shift notes for all ${(participantLabel || "participant").toLowerCase()}s`,
        {
          toastId: TOAST_IDS.TIMESHEET_SHIFT_NOTES_REQUIRED,
        }
      );
      return;
    }
    setShiftNotesErrors({});

    const startDateStr = dateModifiedForTimesheet(effectiveDetails?.start_time);
    const endDateStr = resolveEndDateStrForSave({
      selectedDate: selectedEndDate,
      startDateStr,
      existingEndDateStr: dateModifiedForTimesheet(effectiveDetails?.end_time),
      startTime: watchFormData?.startTime,
      endTime: watchFormData?.endTime,
      crossOverEnabled: crossOver,
    });

    const startDateTime = manuallyEmbedDateWithTime(
      watchFormData?.startTime,
      startDateStr
    );
    const endDateTime = manuallyEmbedDateWithTime(
      watchFormData?.endTime,
      endDateStr
    );

    if (!startDateTime || startDateTime === "00:00:00" || !endDateTime || endDateTime === "00:00:00") {
      toast.error("Schedule time is invalid. Please ensure start and end dates are available.", {
        toastId: TOAST_IDS.TIMESHEET_TIME_VALIDATION_SCHEDULE,
      });
      return;
    }
    if (endDateTime <= startDateTime) {
      toast.error("Schedule time is invalid. End time must be after start time.", {
        toastId: TOAST_IDS.TIMESHEET_TIME_VALIDATION_SCHEDULE
      });
      return;
    }

    const actualStartDateStr = dateModifiedForTimesheet(
      effectiveDetails?.worker_start_time
    );
    const actualEndDateStr = resolveEndDateStrForSave({
      selectedDate: selectedActualEndDate,
      startDateStr: actualStartDateStr,
      existingEndDateStr: dateModifiedForTimesheet(
        effectiveDetails?.worker_end_time
      ),
      startTime: watchFormData?.actualStartTime,
      endTime: watchFormData?.actualEndTime,
      crossOverEnabled: crossOver,
    });

    const actualStartDateTime = manuallyEmbedDateWithTime(
      watchFormData?.actualStartTime,
      actualStartDateStr
    );
    const actualEndDateTime = manuallyEmbedDateWithTime(
      watchFormData?.actualEndTime,
      actualEndDateStr
    );
    if (
      !actualStartDateTime ||
      actualStartDateTime === "00:00:00" ||
      !actualEndDateTime ||
      actualEndDateTime === "00:00:00"
    ) {
      toast.error("Actual time is invalid", {
        toastId: TOAST_IDS.TIMESHEET_TIME_VALIDATION_ACTUAL,
      });
      return;
    }
    if (actualEndDateTime <= actualStartDateTime) {
      toast.error("Actual time is invalid",{
        toastId: TOAST_IDS.TIMESHEET_TIME_VALIDATION_ACTUAL
      });
      return;
    }

    const originalScheduleStart = clockHm(
      extractTimeForInput(effectiveDetails?.start_time)
    );
    const originalScheduleEnd = clockHm(
      extractTimeForInput(effectiveDetails?.end_time)
    );
    const selectedScheduleEndDay = selectedEndDate
      ? moment(selectedEndDate).format("YYYY-MM-DD")
      : null;
    const originalScheduleEndDay = moment(effectiveDetails?.end_time).isValid()
      ? moment(effectiveDetails.end_time).format("YYYY-MM-DD")
      : null;
    const scheduledTimeChanged =
      clockHm(watchFormData?.startTime) !== originalScheduleStart ||
      clockHm(watchFormData?.endTime) !== originalScheduleEnd ||
      (selectedScheduleEndDay != null &&
        selectedScheduleEndDay !== originalScheduleEndDay);

    if (
      isBasicSplitShift &&
      scheduledTimeChanged &&
      !splitResetConfirmedRef.current
    ) {
      setShowSplitResetWarning(true);
      return;
    }
    const resetSplitShift = isBasicSplitShift && scheduledTimeChanged;
    splitResetConfirmedRef.current = false;

    setLoader(true);
    // const filteredNotes = notes.filter((noteObj) => noteObj.notes !== "" && noteObj.notes !== null);
    let valueOfShiftNotes = [];
    if (selectedShiftNotes && typeof selectedShiftNotes === "object") {
      valueOfShiftNotes = Object.values(selectedShiftNotes)
        .filter(({ id }) => id)
        .map(({ id, note }) => ({ pt_id: id, notes: note, id: null }));
    }

    const payload = {
      km: data?.km,

      incident: data?.incidentDetails,
      notes: [
        ...notes.map(({ pt_id, notes: noteText, id }) => ({
          pt_id,
          notes: noteText,
          ...(id != null && id !== "" ? { id } : {}),
        })),
        ...valueOfShiftNotes,
      ],
      comment: data.shiftCost,
      cross_over: crossOver || false,
      // overtime_rule: overtimeRule || false,

      shift_date: moment
        .utc(moment(effectiveDetails?.time_sheet_date).format("YYYY-MM-DD"))
        .format("YYYY-MM-DDT00:00:00.000[Z]"),
    
      start_time: startDateTime,
      end_time: endDateTime,
      worker_start_time: actualStartDateTime,
      worker_end_time: actualEndDateTime,
      // end_time: selectedEndDate
      //   ? convertTimeToDatetimeLocal(
      //       moment(selectedEndDate),
      //       watchFormData?.endTime
      //     )
      //   : 
      //  scheduleDate && convertTimeToDatetimeLocal(
      //       moment(scheduleDate, 'DD/MM/YYYY').format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ'),
      //       watchFormData?.endTime
      //     ) ,
        // moment(effectiveDetails.end_time)
        //     .startOf("day")
        //     .diff(moment(effectiveDetails.start_time).startOf("day"), "days") !== 1 &&
        //   isCheckNextDayShift(
        //     effectiveDetails?.end_time,
        //     watchFormData?.startTime,
        //     watchFormData?.endTime
        //   ) !== false
        // ? isCheckNextDayShift(
        //     effectiveDetails?.end_time,
        //     watchFormData?.startTime,
        //     watchFormData?.endTime
        //   )
        // : convertTimeToDatetimeLocal(
        //     moment(effectiveDetails?.end_time),
        //     watchFormData?.endTime
        //   ),
      // worker_start_time: convertTimeToDatetimeLocal(
      //   moment(effectiveDetails?.worker_start_time),
      //   watchFormData?.actualStartTime
      // ),
      // worker_end_time: selectedActualEndDate
      //   ? convertTimeToDatetimeLocal(
      //       moment(selectedActualEndDate),
      //       watchFormData?.actualEndTime
      //     )
      //   : 
      //    actualDate && convertTimeToDatetimeLocal(
      //       moment(actualDate, 'DD/MM/YYYY').format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ'),
      //       watchFormData?.actualEndTime
      //     ) ,
        // moment(effectiveDetails.worker_end_time)
        //     .startOf("day")
        //     .diff(moment(effectiveDetails.worker_start_time).startOf("day"), "days") !==
        //     1 &&
        //   isCheckNextDayShift(
        //     effectiveDetails?.worker_end_time,
        //     watchFormData?.actualStartTime,
        //     watchFormData?.actualEndTime
        //   ) !== false
        // ? isCheckNextDayShift(
        //     effectiveDetails?.worker_end_time,
        //     watchFormData?.actualStartTime,
        //     watchFormData?.actualEndTime
        //   )
        // : convertTimeToDatetimeLocal(
        //     moment(effectiveDetails?.worker_end_time),
        //     watchFormData?.actualEndTime
        //   ),
      timesheet_length: minutesToHHMM(
        schedule_time
          ? getShiftDurationMinutesFromParts({
              startTime: watchFormData?.startTime,
              endTime: watchFormData?.endTime,
              startDateSource: effectiveDetails?.start_time,
              endDateSource: selectedEndDate ?? effectiveDetails?.end_time,
            })
          : getShiftDurationMinutesFromParts({
              startTime: watchFormData?.actualStartTime,
              endTime: watchFormData?.actualEndTime,
              startDateSource: effectiveDetails?.worker_start_time,
              endDateSource:
                selectedActualEndDate ?? effectiveDetails?.worker_end_time,
            })
      ),
      default_time: schedule_time ? "scheduled" : "actual",
      ...(resetSplitShift ? { reset_split_shift: true } : {}),
    };

    // Shift-level sleepover (basic / basic split / advance) — only when section is shown.
    if (showSleepoverSection) {
      const sleepoverOn = parseBooleanVal(watchFormData?.sleepover);
      Object.assign(payload, {
        sleepover: sleepoverOn,
        active_hours: sleepoverOn
          ? String(watchFormData?.active_hours ?? "").trim()
          : "",
      });
    }
 
    if (effectiveDetails?.worker_id) {
      Object.assign(payload, {
        worker_id: effectiveDetails?.worker_id,
      });
    }
    if (
      roleAccess([USER_ROLE.Sp_Admin]) &&
      currentLoginUser &&
      currentLoginUser?.id
    ) {
      Object.assign(payload, {
        updated_by_user: currentLoginUser?.id,
      });
    }

    if (roleAccess([USER_ROLE.Sp_manager]) && currentLoginUser) {
      Object.assign(payload, {
        updated_by_user:
          currentLoginUser?.sp_id || currentLoginUser?.service_provider?.id,
      });
    }

    Object.assign(payload, {
      shift_instructions: data?.shift_instructions,
    });
    apiPostRequest(
      `${URL_ROSTERING_TIMESHEET_DETAILS_UPDATE}/${effectiveDetails?.uuid}`,
      payload
    )
      .then((res) => {
        setLoader(false);
        if (res?.status === 200) {
          toast.success("Timesheet details updated successfully");
          clearErrors();
          setShiftNotesErrors({});
          const updated = res?.data?.data;
          setShiftDetails(updated);
          syncTimesheetTableRowFromDetail(dispatch, updated);
        }
      })
      .catch((err) => {
        setLoader(false);
        toast.error(err.message || "Something went wrong");
        console.error("Error updating timesheet details:", err);
      });
  };

  // useEffect(() => {
  //   setSelectedEndDate(null);
  // }, [watchFormData?.startTime, watchFormData?.endTime]);

  // useEffect(() => {
  //   setSelectedActualEndDate(null);
  // }, [watchFormData?.actualStartTime, watchFormData?.actualEndTime]);
  const [isActualEndTimeChanged, setIsActualEndTimeChanged] = useState(false);
  const [endTimeChanged, setEndTimeChanged] = useState(false);

  useEffect(() => {
    if (!effectiveDetails?.start_time || !effectiveDetails?.end_time) {
      setIsScheduleDate("");
      return;
    }
    const start = moment.utc(effectiveDetails.start_time).startOf("day");
    const end = moment.utc(effectiveDetails.end_time).startOf("day");
    let result = "";
    if (end.diff(start, "days") === 1) {
      
      if (endTimeChanged) {
     
        if (watchFormData?.endTime < watchFormData?.startTime) {
      
          result = moment.utc(effectiveDetails.end_time).format("DD/MM/YYYY");
        } else {
     
          result = moment.utc(effectiveDetails.start_time).format("DD/MM/YYYY");
        }
      } else {
     
        result = moment.utc(effectiveDetails.end_time).format("DD/MM/YYYY");
      }
    } else if (
      effectiveDetails.end_time &&
      isEndTimeNextDay(
        effectiveDetails?.start_time,
        watchFormData?.startTime,
        watchFormData?.endTime,
        effectiveDetails?.end_time
      )
    ) {
      result = customFormatDate(
        convertTimeToDatetimeLocalWithoutMoment(
          effectiveDetails?.start_time,
          watchFormData?.endTime,
          true
        )
      );
    } else {
      result = customFormatDate(
        convertTimeToDatetimeLocalWithoutMoment(
          effectiveDetails?.start_time,
          watchFormData?.endTime
        )
      );
    }


    setIsScheduleDate(result);
  }, [
    effectiveDetails?.start_time,
    effectiveDetails?.end_time,
    watchFormData?.startTime,
    watchFormData?.endTime,
    endTimeChanged,
  ]);

  useEffect(() => {
    if (!effectiveDetails?.worker_start_time || !effectiveDetails?.worker_end_time) {
      setActualDate("");
      return;
    }
    const start = moment.utc(effectiveDetails.worker_start_time).startOf("day");
    const end = moment.utc(effectiveDetails.worker_end_time).startOf("day");
    let result = "";
    if (end.diff(start, "days") === 1) {
      
      if (isActualEndTimeChanged) {
     
        if (watchFormData?.actualEndTime < watchFormData?.actualStartTime) {
      
          result = moment.utc(effectiveDetails.worker_end_time).format("DD/MM/YYYY");
        } else {
     
          result = moment.utc(effectiveDetails.worker_start_time).format("DD/MM/YYYY");
        }
      } else {
     
        result = moment.utc(effectiveDetails.worker_end_time).format("DD/MM/YYYY");
      }
    } else if (
      effectiveDetails.worker_end_time &&
      isEndTimeNextDay(
        effectiveDetails?.worker_start_time,
        watchFormData?.actualStartTime,
        watchFormData?.actualEndTime,
        effectiveDetails?.worker_end_time
      )
    ) {
      result = customFormatDate(
        convertTimeToDatetimeLocalWithoutMoment(
          effectiveDetails?.worker_start_time,
          watchFormData?.actualEndTime,
          true
        )
      );
    } else {
      result = customFormatDate(
        convertTimeToDatetimeLocalWithoutMoment(
          effectiveDetails?.worker_start_time,
          watchFormData?.actualEndTime
        )
      );
    }


    setActualDate(result);
  }, [
    effectiveDetails?.worker_start_time,
    effectiveDetails?.worker_end_time,
    watchFormData?.actualStartTime,
    watchFormData?.actualEndTime,
    isActualEndTimeChanged,
  ]);

  const scheduledMinutes = getShiftDurationMinutesFromParts({
    startTime: watchFormData?.startTime,
    endTime: watchFormData?.endTime,
    startDateSource: effectiveDetails?.start_time,
    endDateSource: selectedEndDate ?? effectiveDetails?.end_time,
  });
  const actualMinutes = getShiftDurationMinutesFromParts({
    startTime: watchFormData?.actualStartTime,
    endTime: watchFormData?.actualEndTime,
    startDateSource: effectiveDetails?.worker_start_time,
    endDateSource: selectedActualEndDate ?? effectiveDetails?.worker_end_time,
  });
  let actualDurationSuffix = "";
  if (
    watchFormData?.startTime &&
    watchFormData?.actualStartTime &&
    effectiveDetails?.start_time &&
    effectiveDetails?.worker_start_time
  ) {
    const schedStart = buildShiftDateTime(
      watchFormData.startTime,
      effectiveDetails.start_time
    );
    const actualStart = buildShiftDateTime(
      watchFormData.actualStartTime,
      effectiveDetails.worker_start_time
    );
    if (schedStart?.isValid() && actualStart?.isValid()) {
      const diffMin = actualStart.diff(schedStart, "minutes");
      if (diffMin > 0) {
        actualDurationSuffix = ` · ${formatDurationMinutes(diffMin)} late start`;
      } else if (diffMin < 0) {
        actualDurationSuffix = ` · ${formatDurationMinutes(diffMin)} early start`;
      }
    }
  }
  // Advance (V5): hide shared-shift / ratio strip. Basic (and basic split) keep existing behaviour.
  const showSharedStrip =
    !isAdvanceTimesheet &&
    (effectiveDetails?.is_group_shift ||
      (effectiveDetails?.ratio && effectiveDetails.ratio !== "1:1"));
  const participants = effectiveDetails?.participants ?? [];

  const revertSplitShiftTimes = () => {
    setValue("startTime", extractTimeForInput(effectiveDetails?.start_time));
    setValue("endTime", extractTimeForInput(effectiveDetails?.end_time));
    setValue(
      "actualStartTime",
      extractTimeForInput(effectiveDetails?.worker_start_time)
    );
    setValue(
      "actualEndTime",
      extractTimeForInput(effectiveDetails?.worker_end_time)
    );
    setSelectedEndDate(null);
    setSelectedActualEndDate(null);
    setEndTimeChanged(false);
    setIsActualEndTimeChanged(false);
  };

  const handleSplitResetCancel = () => {
    splitResetConfirmedRef.current = false;
    setShowSplitResetWarning(false);
    revertSplitShiftTimes();
  };

  const handleSplitResetConfirm = () => {
    splitResetConfirmedRef.current = true;
    setShowSplitResetWarning(false);
    handleSubmit(onSubmit)();
  };

  const handleParticipantNoteChange = (ptId, value) => {
    if (shiftNotesErrors[ptId]) {
      setShiftNotesErrors((prev) => {
        const next = { ...prev };
        delete next[ptId];
        return next;
      });
    }

    const noteIndex = notes.findIndex((n) => n.pt_id === ptId);
    if (noteIndex >= 0) {
      handleNoteChange(noteIndex, value);
    } else {
      handleNewShiftNotes(ptId, value);
    }
  };

  return (
    <div className="timesheet-detail-form min-h-0 w-full">
      {shiftDetailsLoader && !effectiveDetails ? (
        <TimesheetDetailsTabSkeleton />
      ) : !effectiveDetails ? null : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <TimesheetModeBanner shiftDetails={shiftDetails} lane="details" />
          {showSharedStrip && (
            <TimesheetSharedShiftStrip
              compact
              title={`Shared shift · Ratio ${effectiveDetails.ratio} (Worker:Participant)`}
            />
          )}

          <TimesheetInlineDatePicker
            open={isEndDatePickerOpen}
            anchorRef={scheduledEndDateAnchorRef}
            selected={selectedEndDate}
            onChange={(date) => {
              setSelectedEndDate(date);
            }}
            onClose={() => setIsEndDatePickerOpen(false)}
            minDate={
              effectiveDetails?.start_time
                ? moment(effectiveDetails.start_time, "YYYY-MM-DD HH:mm:ss")
                    .startOf("day")
                    .toDate()
                : null
            }
            maxDate={
              effectiveDetails?.start_time
                ? moment(effectiveDetails.start_time, "YYYY-MM-DD HH:mm:ss")
                    .startOf("day")
                    .add(1, "days")
                    .toDate()
                : null
            }
          />
          <TimesheetInlineDatePicker
            open={isActualEndDatePickerOpen}
            anchorRef={actualEndDateAnchorRef}
            selected={selectedActualEndDate}
            onChange={(date) => {
              setSelectedActualEndDate(date);
            }}
            onClose={() => setIsActualEndDatePickerOpen(false)}
            minDate={
              effectiveDetails?.worker_start_time
                ? moment(effectiveDetails.worker_start_time, "YYYY-MM-DD HH:mm:ss")
                    .startOf("day")
                    .toDate()
                : null
            }
            maxDate={
              effectiveDetails?.worker_start_time
                ? moment(effectiveDetails.worker_start_time, "YYYY-MM-DD HH:mm:ss")
                    .startOf("day")
                    .add(1, "days")
                    .toDate()
                : null
            }
          />

          <TimesheetCrossOverToggleRow
            value={crossOver}
            disabled={isStatusApproved}
            onChange={setCrossOver}
          />

          <div className="td-fields-grid">
              {showSleepoverSection && (
                <TimesheetSleepoverActiveHoursRow
                  sleepover={watchFormData?.sleepover}
                  disabled={isStatusApproved}
                  onSleepoverChange={(value) => {
                    setValue("sleepover", value, { shouldDirty: true });
                    if (!value) {
                      setValue("active_hours", "", { shouldDirty: true });
                    }
                  }}
                  activeHours={watchFormData?.active_hours || ""}
                  onActiveHoursChange={(value) => {
                    setValue("active_hours", sanitizeActiveHoursInput(value), {
                      shouldDirty: true,
                    });
                  }}
                />
              )}
              <TimesheetTimeSection
                title="Scheduled start / finish"
                startLabel={`Start (${effectiveDetails?.start_time ? dateModifiedForTimesheet(effectiveDetails.start_time) : "—"})`}
                endLabel={
                  <span className="td-time-slot-label td-time-slot-label--end">
                    End (
                    {selectedEndDate
                      ? moment(selectedEndDate).format("DD/MM/YYYY")
                      : effectiveDetails?.end_time
                      ? dateModifiedForTimesheet(effectiveDetails.end_time)
                      : "—"}
                    )
                    {!isStatusApproved && (
                      <button
                        ref={scheduledEndDateAnchorRef}
                        type="button"
                        className="td-end-time-edit"
                        onClick={() => setIsEndDatePickerOpen(true)}
                        aria-label="Change scheduled end date"
                      >
                        <FaPencilAlt className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                }
                startInput={
                  <input
                    type="time"
                    name="startTime"
                    disabled={isStatusApproved}
                    {...register("startTime", {
                      required: "Start time is required",
                    })}
                    className={errors.startTime ? "border-red-500" : ""}
                  />
                }
                startError={
                  errors.startTime && (
                    <p className="text-red-500 text-xs">
                      {errors.startTime.message}
                    </p>
                  )
                }
                endInput={
                  <input
                    type="time"
                    name="endTime"
                    disabled={isStatusApproved}
                    {...register("endTime", {
                      required: "end time is required",
                      onChange: () => {
                        setEndTimeChanged(true);
                      },
                    })}
                    className={errors.endTime ? "border-red-500" : ""}
                  />
                }
                endError={
                  errors.endTime && (
                    <p className="text-red-500 text-xs">
                      {errors.endTime.message}
                    </p>
                  )
                }
                durationText={
                  scheduledMinutes != null
                    ? `Duration: ${formatDurationMinutes(scheduledMinutes)} planned`
                    : null
                }
              />

              <TimesheetTimeSection
                title="Actual start / finish"
                startLabel={`Start (${effectiveDetails?.worker_start_time ? dateModifiedForTimesheet(effectiveDetails.worker_start_time) : "—"})`}
                endLabel={
                  <span className="td-time-slot-label td-time-slot-label--end">
                    End (
                    {selectedActualEndDate
                      ? moment(selectedActualEndDate).format("DD/MM/YYYY")
                      : effectiveDetails?.worker_end_time
                      ? dateModifiedForTimesheet(effectiveDetails.worker_end_time)
                      : "—"}
                    )
                    {!isStatusApproved && (
                      <button
                        ref={actualEndDateAnchorRef}
                        type="button"
                        className="td-end-time-edit"
                        onClick={() => setIsActualEndDatePickerOpen(true)}
                        aria-label="Change actual end date"
                      >
                        <FaPencilAlt className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                }
                startInput={
                  <input
                    type="time"
                    name="actualStartTime"
                    disabled={isStatusApproved}
                    {...register("actualStartTime", {
                      required: "actual start time is required",
                    })}
                    className={errors.actualStartTime ? "border-red-500" : ""}
                  />
                }
                startError={
                  errors.actualStartTime && (
                    <p className="text-red-500 text-xs">
                      {errors.actualStartTime.message}
                    </p>
                  )
                }
                endInput={
                  <input
                    type="time"
                    name="actualEndTime"
                    disabled={isStatusApproved}
                    {...register("actualEndTime", {
                      required: "actual end time is required",
                      onChange: () => {
                        setIsActualEndTimeChanged(true);
                      },
                    })}
                    className={errors.actualEndTime ? "border-red-500" : ""}
                  />
                }
                endError={
                  errors.actualEndTime && (
                    <p className="text-red-500 text-xs">
                      {errors.actualEndTime.message}
                    </p>
                  )
                }
                durationText={
                  actualMinutes != null
                    ? `Duration: ${formatDurationMinutes(actualMinutes)} worked${actualDurationSuffix}`
                    : null
                }
                durationWarning={actualDurationSuffix.includes("late")}
              />

              <TimesheetCompliancePanel
                incidentField={
                  <textarea
                    rows="3"
                    disabled={isStatusApproved}
                    placeholder="Describe any incidents during the shift…"
                    {...register("incidentDetails", {
                      required: "Incident details are required",
                    })}
                    className={`w-full ${
                      errors.incidentDetails ? "border-red-500" : ""
                    }`}
                    spellCheck="false"
                  />
                }
                incidentError={
                  errors.incidentDetails && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.incidentDetails.message}
                    </p>
                  )
                }
              />

              <TimesheetCostsPanel
                costField={
                  <input
                    type="text"
                    placeholder="Enter cost…"
                    disabled={isStatusApproved}
                    name="shiftCost"
                    {...register("shiftCost")}
                  />
                }
                costError={
                  errors?.shiftCost && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors?.shiftCost?.message}
                    </span>
                  )
                }
                kmField={
                  <input
                    type="number"
                    name="additionalCost"
                    disabled={isStatusApproved}
                    step="0.01"
                    placeholder="Enter km"
                    {...register("km", {
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: "Km cannot be negative",
                      },
                      max: {
                        value: 999,
                        message: "Please enter a valid KM value",
                      },
                    })}
                  />
                }
                kmError={
                  errors?.km && (
                    <span className="text-red-500 text-xs">
                      {errors?.km?.message}
                    </span>
                  )
                }
              />

            {participants.length > 0 && (
              <div className="td-participant-span">
                {participants.length > 1 ? (
                  <p className="td-participant-section-title">
                    Participant shift notes
                  </p>
                ) : null}
                {participants.map((participant, index) => {
                  const ptId = participant.id;
                  const noteObj = notes?.find((n) => n.pt_id === ptId);

                  return (
                    <TimesheetParticipantNotesPanel
                      key={ptId ?? `participant-${index}`}
                      participant={participant}
                      notesLabel="Shift notes"
                      notesRequired
                      notesError={
                        shiftNotesErrors[ptId] && (
                          <p className="text-red-500 text-xs mt-1">
                            {shiftNotesErrors[ptId]}
                          </p>
                        )
                      }
                      notesField={
                        <textarea
                          className={`w-full ${
                            shiftNotesErrors[ptId] ? "border-red-500" : ""
                          }`}
                          rows="3"
                          disabled={isStatusApproved}
                          placeholder="Add shift notes for this participant…"
                          value={
                            noteObj?.notes ||
                            selectedShiftNotes[ptId]?.note ||
                            ""
                          }
                          onChange={(e) =>
                            handleParticipantNoteChange(ptId, e.target.value)
                          }
                          spellCheck="false"
                        />
                      }
                      instructionsLabel="Shift instructions"
                      instructionsField={
                        <>
                          <input
                            type="hidden"
                            {...register(`shift_instructions[${index}].pt_id`)}
                          />
                          <textarea
                            className="w-full"
                            rows="3"
                            disabled={isStatusApproved}
                            placeholder="Shift instructions from roster…"
                            {...register(
                              `shift_instructions[${index}].shift_instruction`
                            )}
                            spellCheck="false"
                          />
                        </>
                      }
                      showInstructions
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div className="td-form-actions">
            <TimesheetDetailsSaveBar
              loading={loader}
              disabled={loader || isStatusApproved}
            />
          </div>
        </form>
      )}
      {showSplitResetWarning && (
        <div
          className="ca-shift-time-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="split-shift-time-warning-title"
        >
          <div className="ca-shift-time-modal">
            <span className="ca-shift-time-modal-icon">
              <HiOutlineExclamationTriangle className="h-6 w-6" aria-hidden />
            </span>
            <h3
              id="split-shift-time-warning-title"
              className="ca-shift-time-modal-title"
            >
              Reset split shift?
            </h3>
            <p className="ca-shift-time-modal-body">
              Changing the scheduled time will reset the existing split shift
              configuration. Any existing split segments and their allocations
              may be removed. Do you want to continue?
            </p>
            <div className="ca-shift-time-modal-actions">
              <button
                type="button"
                className="ca-shift-time-modal-btn ca-shift-time-modal-btn--cancel"
                onClick={handleSplitResetCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ca-shift-time-modal-btn ca-shift-time-modal-btn--proceed"
                onClick={handleSplitResetConfirm}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(TimeSheetDetails);
